import { format } from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Pencil,
  Ban,
  RotateCcw,
  ShieldCheck,
  ShieldOff,
  Lock,
} from "lucide-react";
import type {
  Organization,
  OrganizationSSOSummary,
  OrganizationType,
  OrganizationTier,
} from "../types";
import { ORG_TYPE_LABEL_KEYS, ORG_TYPE_EMOJI, ORG_TIER_LABELS } from "../constants";
import { OrganizationStatusBadge } from "./OrganizationStatusBadge";
import { useLocale } from "../../../context/LanguageContext";

interface OrganizationTableProps {
  organizations: Organization[];
  total: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onEdit: (org: Organization) => void;
  onDrillDown: (org: Organization) => void;
  onSuspend: (org: Organization) => void;
  onReactivate: (org: Organization) => void;
}

export function OrganizationTable({
  organizations,
  total,
  isLoading,
  page,
  pageSize,
  onPageChange,
  onEdit,
  onDrillDown,
  onSuspend,
  onReactivate,
}: OrganizationTableProps) {
  const { t } = useLocale();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, total);

  if (isLoading && organizations.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-14 animate-pulse rounded-lg border border-border bg-card"
          />
        ))}
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
        <span className="text-4xl">🏢</span>
        <p className="mt-3 text-sm font-medium text-foreground">
          {t("organizations.table.emptyTitle")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("organizations.table.emptyHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("organizations.field.organization")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("organizations.field.type")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("organizations.field.tier")}
              </th>
              <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                SSO
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                {t("organizations.field.members")}
              </th>
              <th className="px-4 py-3 text-center font-medium text-muted-foreground">
                {t("organizations.field.status")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("organizations.field.created")}
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                {t("organizations.field.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => {
              const typeKey = ORG_TYPE_LABEL_KEYS[org.type as OrganizationType];
              const typeLabel = typeKey ? t(typeKey) : org.type;
              const typeEmoji =
                ORG_TYPE_EMOJI[org.type as OrganizationType] ?? "🏗️";

              return (
                <tr
                  key={org._id}
                  className="border-b border-border last:border-b-0 hover:bg-muted/30"
                >
                  {/* Organization Name */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {org.picture ? (
                        <img
                          src={org.picture}
                          alt={org.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm">
                          {org.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-foreground">
                          {org.name}
                        </div>
                        {org.website && (
                          <div className="text-[11px] text-muted-foreground truncate max-w-[200px]">
                            {org.website}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Type */}
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-foreground">
                      <span>{typeEmoji}</span>
                      <span>{typeLabel}</span>
                    </span>
                  </td>

                  {/* Tier */}
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                      {ORG_TIER_LABELS[org.tier as OrganizationTier] ?? org.tier ?? "—"}
                    </span>
                  </td>

                  {/* SSO */}
                  <td className="whitespace-nowrap px-4 py-3 text-center">
                    <SSOStatusBadge sso={org.sso ?? null} />
                  </td>

                  {/* Members */}
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-foreground">
                    {org.memberCount.toLocaleString()}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 text-center">
                    <OrganizationStatusBadge isActive={org.isActive} />
                  </td>

                  {/* Created */}
                  <td className="whitespace-nowrap px-4 py-3 text-foreground">
                    {format(new Date(org.createdAt), "MMM d, yyyy")}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onDrillDown(org)}
                        title={t("adminUI.organizations.viewAsOrg")}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onEdit(org)}
                        title={t("adminUI.organizations.edit")}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {org.isActive ? (
                        <button
                          type="button"
                          onClick={() => onSuspend(org)}
                          title={t("adminUI.organizations.suspend")}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Ban className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onReactivate(org)}
                          title={t("adminUI.organizations.reactivate")}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-success/10 hover:text-success"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <p className="text-xs text-muted-foreground">
            {t("organizations.table.showingPrefix")}{" "}
            {t("common.showingRange", { start: startRow, end: endRow, total })}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-xs text-muted-foreground">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SSOStatusBadge({ sso }: { sso: OrganizationSSOSummary | null }) {
  const { t } = useLocale();
  if (!sso || !sso.enabled) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
        title={t("adminUI.organizations.ssoDisabled")}
      >
        <ShieldOff className="h-3 w-3" />
        {t("organizations.sso.off")}
      </span>
    );
  }
  if (sso.enforced) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning"
        title={t("adminUI.organizations.ssoEnforced")}
      >
        <Lock className="h-3 w-3" />
        {t("organizations.sso.enforced")}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success"
      title={t("adminUI.organizations.ssoOptional")}
    >
      <ShieldCheck className="h-3 w-3" />
      {t("organizations.sso.on")}
    </span>
  );
}
